import Phaser from 'phaser';

/**
 * Dymek edukacyjny nad stworkiem-flagą (biom „Świat Flag").
 *
 * Pokazuje na żądanie (z `EduInteractBar`) zwrot w języku kraju (duży tekst) +
 * tłumaczenie po polsku (mniejszy tekst) w „dymku". Pojawia się z lekkim popem,
 * przez chwilę wisi, po czym zanika i sam się usuwa.
 *
 * Zwraca uchwyt z `update(x, y)` (żeby WorldScene mógł go przesuwać za
 * poruszającym się petem) i `destroy()` (sprzątanie przy zmianie biomu).
 * To rodzeństwo `showDamagePopup` — ta sama filozofia „tekst nad obiektem bez
 * dedykowanych assetów", ale jako interaktywny, sterowany przez gracza dymek.
 */

const FADE_IN_MS = 150;
const HOLD_MS = 3000;
const FADE_OUT_MS = 320;
const PAD_X = 22;
const PAD_Y = 14;
const OFFSET_Y = 90; // jak wysoko nad środkiem peta (duży dymek → wyżej, by nie zasłaniał stworka)
const VIEWPORT_MARGIN = 10; // min odstęp dymka od krawędzi widocznego obszaru kamery

/** Clampuje środek dymka (halfW/halfH = połowa wymiarów) do worldView kamery. */
function clampPos(
  scene: Phaser.Scene,
  rawX: number,
  rawY: number,
  halfW: number,
  halfH: number,
): { x: number; y: number } {
  const v = scene.cameras.main.worldView;
  return {
    x: Phaser.Math.Clamp(rawX, v.x + halfW + VIEWPORT_MARGIN, v.right  - halfW - VIEWPORT_MARGIN),
    y: Phaser.Math.Clamp(rawY, v.y + halfH + VIEWPORT_MARGIN, v.bottom - halfH - VIEWPORT_MARGIN),
  };
}

export interface EduBubble {
  /** Czas (scene.time.now), po którym dymek sam się usunął. */
  readonly expiresAt: number;
  /** Przesuń dymek nad nową pozycję peta. */
  update(x: number, y: number): void;
  /** Natychmiastowe usunięcie (np. przy zmianie biomu). */
  destroy(): void;
}

export function showEduBubble(
  scene: Phaser.Scene,
  x: number,
  y: number,
  native: string,
  pl: string,
): EduBubble {
  // Tymczasowa pozycja (0,0) — ustawimy po obliczeniu boxW/boxH (clamp do viewportu).
  const container = scene.add.container(0, 0).setDepth(75);

  const nativeText = scene.add
    .text(0, 0, native, {
      fontFamily: 'Baloo 2, sans-serif',
      fontSize: '40px',
      color: '#1f4f7a',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: 360 },
    })
    .setOrigin(0.5);

  const plText = scene.add
    .text(0, 0, pl, {
      fontFamily: 'Baloo 2, sans-serif',
      fontSize: '24px',
      color: '#3a6b8a',
      align: 'center',
      wordWrap: { width: 340 },
    })
    .setOrigin(0.5);

  const contentW = Math.max(nativeText.width, plText.width);
  const boxW = contentW + PAD_X * 2;
  const boxH = nativeText.height + plText.height + 4 + PAD_Y * 2;

  // Stack tekstów wewnątrz dymka (native nad pl).
  nativeText.setY(-boxH / 2 + PAD_Y + nativeText.height / 2);
  plText.setY(nativeText.y + nativeText.height / 2 + plText.height / 2 + 2);

  const bg = scene.add.graphics();
  bg.fillStyle(0xffffff, 0.96);
  bg.lineStyle(4, 0x4f8fd8, 1);
  bg.fillRoundedRect(-boxW / 2, -boxH / 2, boxW, boxH, 18);
  bg.strokeRoundedRect(-boxW / 2, -boxH / 2, boxW, boxH, 18);
  // Ogonek dymka skierowany w dół (ku petowi).
  bg.fillStyle(0xffffff, 0.96);
  bg.fillTriangle(-12, boxH / 2 - 1, 12, boxH / 2 - 1, 0, boxH / 2 + 15);

  container.add([bg, nativeText, plText]);

  // Ustaw pozycję po obliczeniu wymiarów — clamp do widocznego obszaru kamery.
  const halfW = boxW / 2;
  const halfH = boxH / 2;
  const initPos = clampPos(scene, x, y - OFFSET_Y, halfW, halfH);
  container.setPosition(initPos.x, initPos.y);

  container.setAlpha(0).setScale(0.85);

  scene.tweens.add({
    targets: container,
    alpha: 1,
    scale: 1,
    duration: FADE_IN_MS,
    ease: 'Back.easeOut',
  });

  const fadeOut = scene.tweens.add({
    targets: container,
    alpha: 0,
    delay: FADE_IN_MS + HOLD_MS,
    duration: FADE_OUT_MS,
    ease: 'Cubic.easeIn',
    onComplete: () => container.destroy(),
  });

  return {
    expiresAt: scene.time.now + FADE_IN_MS + HOLD_MS + FADE_OUT_MS,
    update(nx: number, ny: number): void {
      if (container.active) {
        const p = clampPos(scene, nx, ny - OFFSET_Y, halfW, halfH);
        container.setPosition(p.x, p.y);
      }
    },
    destroy(): void {
      fadeOut.stop();
      if (container.active) container.destroy();
    },
  };
}
